// [UI-COMBAT-STATUS-STRIP — 2026-07-11]: Persistent one-line vitals strip
//   inside the sticky app header, visible on every tab.
// [WHY]: Fleet ui-qol #2 (golden-goose 2026-07-11): vitals were visible on 1
//   of 21 tabs — a player who tabs to Inventory or Spellbook mid-fight goes
//   blind on HP/AC/conditions. HP, defenses, and conditions are universal
//   primitives every system needs surfaced identically (endgame note on the
//   board row) — this strip is system-agnostic infrastructure, not D&D chrome.
// [DATA — Rule 2/17]: AC via effectCalculator.calculateAC (never hand-rolled);
//   HP via canonical playerStats.currentHitPoints/maxHitPoints/
//   temporaryHitPoints; conditions via character.conditions ids resolved
//   through buildConditionsMap (SRD + custom); death saves via the
//   playerStats.deathSaves shape shared with utils/deathSaveOps.js.
// [DQ-4 — user-ruled 2026-07-09]: HP color is GRADED green→yellow→red.
// [UI-HP-BAR-UNIFICATION — 2026-07-25]: this file's local hpGradeClasses WAS the
//   first shipped consumer of that ruling; it has now been LIFTED to
//   src/utils/hpGrade.js as hpChipClasses and is shared by all five HP
//   surfaces. Thresholds and the chip palette are unchanged — the strip is the
//   surface the other four converged onto.
// [RULE 19]: Not a modal — no useModalEscape.

import React, { useMemo } from 'react';
import { Shield } from 'lucide-react';
import { useCharacter } from '../contexts/CharacterContext';
import { useInventory } from '../contexts/InventoryContext';
import { effectCalculator } from '../services/effectCalculator';
import { buildConditionsMap } from '../utils/conditionLookup';
import { hpChipClasses, hpGrade } from '../utils/hpGrade';

const CHIP = 'inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs whitespace-nowrap';
const MAX_CONDITION_CHIPS = 4;

function CombatStatusStrip({ onNavigate }) {
  const { playerStats, activeQuickBonuses } = useCharacter();
  const { inventory } = useInventory();

  // AC through the central calculator — includes armor, DEX rules, feats,
  // items, and quick bonuses (same call VitalStatsCard makes).
  const ac = useMemo(
    () => effectCalculator.calculateAC(playerStats, inventory, activeQuickBonuses),
    [playerStats, inventory, activeQuickBonuses]
  );

  const conditionEntries = useMemo(() => {
    const ids = Array.isArray(playerStats?.conditions) ? playerStats.conditions : [];
    if (ids.length === 0) return [];
    const map = buildConditionsMap(playerStats);
    return ids.map((id) => {
      const cond = map[id];
      return { id, icon: (cond && cond.icon) || '🌀', name: (cond && cond.name) || id };
    });
  }, [playerStats]);

  if (!playerStats || !playerStats.name) return null;

  const current = playerStats.currentHitPoints ?? 0;
  const max = playerStats.maxHitPoints ?? 0;
  const temp = playerStats.temporaryHitPoints || 0;
  // [BUG-MANUAL-AC-IGNORED-BY-STATUS-STRIP — Jordie's ruling 2026-07-16 "yes."]:
  // honour the Manual AC Override with VitalStatsCard's exact precedence
  // (useManualAC ? manualAC || 10 : calculated). manualAC is display-only —
  // zero references in services/utils/contexts — so this cannot feed back
  // into any calculation (the constraint the ruling was conditioned on).
  const shownAc = playerStats.useManualAC ? (playerStats.manualAC || 10) : ac;
  const isDying = current <= 0;
  const deathSaves = playerStats.deathSaves || {};
  const exhaustion = playerStats.exhaustionLevel || 0;
  const shownConditions = conditionEntries.slice(0, MAX_CONDITION_CHIPS);
  const overflow = conditionEntries.length - shownConditions.length;

  return (
    <div className="border-t border-gray-800">
      <button
        type="button"
        onClick={() => onNavigate && onNavigate()}
        title="Open Character tab"
        data-testid="combat-status-strip"
        className="w-full max-w-7xl mx-auto px-4 py-1 flex items-center gap-2 overflow-x-auto text-left hover:bg-gray-800/60 transition-colors"
      >
        {/* HP — graded per DQ-4, via the shared grader (UI-HP-BAR-UNIFICATION) */}
        <span
          className={`${CHIP} font-mono ${hpChipClasses(current, max)}`}
          data-hp-grade={hpGrade(current, max)}
          data-testid="status-strip-hp"
        >
          ❤️ {current}/{max}{temp > 0 ? ` +${temp}` : ''}
        </span>

        {/* Dying state — death-save progress from the shared deathSaves shape */}
        {isDying && (
          <span className={`${CHIP} bg-red-900/60 text-red-200 border-red-600 animate-pulse font-semibold`} data-testid="status-strip-dying">
            DYING ✓{deathSaves.successes || 0} ✗{deathSaves.failures || 0}
          </span>
        )}

        {/* AC */}
        <span className={`${CHIP} bg-slate-800/60 text-slate-200 border-slate-600/40 font-mono`} data-testid="status-strip-ac">
          <Shield size={11} className="text-slate-400" /> {shownAc}
        </span>

        {/* Exhaustion (from timeSystem via the context's computed alias) */}
        {exhaustion > 0 && (
          <span className={`${CHIP} bg-amber-900/40 text-amber-300 border-amber-700/40`} data-testid="status-strip-exhaustion">
            😴 Exhaustion {exhaustion}
          </span>
        )}

        {/* Conditions — first few chips, then a +N overflow */}
        {shownConditions.map((cond) => (
          <span key={cond.id} className={`${CHIP} bg-gray-800 text-gray-300 border-gray-700`} data-testid={`status-strip-condition-${cond.id}`}>
            <span>{cond.icon}</span>
            <span className="hidden sm:inline">{cond.name}</span>
          </span>
        ))}
        {overflow > 0 && (
          <span className={`${CHIP} bg-gray-800 text-gray-400 border-gray-700`} data-testid="status-strip-condition-overflow">
            +{overflow}
          </span>
        )}
      </button>
    </div>
  );
}

export default CombatStatusStrip;
